import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {InlineBanner, InlineBannerTypesEnum} from '../../components/inlineBanner/inlineBanner';
import {accountsLearnMoreLink, setupAccountsUrl} from '../../helpers/accountHelpers';
import {CollapsedItemKeyPrefixesEnum} from '../../models/api/localPreferencesModel';
import {useExpandItem} from '../../store/data/localPreferences/useExpandItem';
import {useFetchHasPotentialAccountRules} from '../fetchers/accounts';
import {settingsHorizontalSpacing} from '../settingsCommonConstants';

/*
 * Props.
 */

interface AccountRulesInfoBoxProps {
  namespace: string;
}

/*
 * Styles.
 */

const StyledInfoBox = styled.div`
  padding-left: ${settingsHorizontalSpacing}px;
  padding-bottom: 12px;
`;

/*
 * Component.
 */

export const AccountRulesInfoBox: FC<AccountRulesInfoBoxProps> = (props) => {
  const infoBoxExpandedKey = `${CollapsedItemKeyPrefixesEnum.SETTINGS_RULES}accountRuleInfoBox`;
  const isInfoBoxExpandedDefault = true;
  const {hasPotentialAccountRules} = useFetchHasPotentialAccountRules(props.namespace);
  const [isInfoBoxOpen, toggleInfoBox] = useExpandItem(infoBoxExpandedKey, isInfoBoxExpandedDefault);
  const {navigateToLink} = useLinkNavigation();

  if (!isInfoBoxOpen || !hasPotentialAccountRules) {
    return null;
  }

  return (
    <StyledInfoBox>
      <InlineBanner
        size={VisualSizesEnum.SMALL}
        type={InlineBannerTypesEnum.TIP}
        title={
          <Trans id="account-rule-infobox-body">
            You can replace many domain-based rules with a single account-based rule by setting up Accounts
          </Trans>
        }
        onClose={toggleInfoBox}
        hasInlineActions
        actions={[
          {
            id: 'learn-more',
            onClick: () => openInBrowser(accountsLearnMoreLink),
            title: <Trans id="account-rule-tip-learn-more">Learn more</Trans>,
            iconName: 'externalLinkSmall',
          },
          {
            id: 'setup-accounts',
            onClick: () => navigateToLink(setupAccountsUrl),
            title: <Trans id="account-rule-tip-setup">Setup Accounts</Trans>,
          },
        ]}
      />
    </StyledInfoBox>
  );
};
